import React, { useEffect, useMemo, useState } from "react";
import { Alert, Button, Chip, Grid, MenuItem, TextField, Typography } from "@mui/material";
import PlaylistAddIcon from "@mui/icons-material/PlaylistAdd";
import EntityHubLayout, { PAGE_SIZE } from "../../components/product-manager/EntityHubLayout";
import EntityAssetCard from "../../components/product-manager/EntityAssetCard";
import EditArchitectureStandardDialog from "../../components/product-manager/EditArchitectureStandardDialog";
import ViewDocumentDialog from "../../components/product-manager/ViewDocumentDialog";
import {
  filterByHubState,
  useBookmarkableHubState,
} from "../../hooks/useBookmarkableHubState";
import { useDocuments } from "../../hooks/useDocuments";
import { apiService } from "../../api/services/apiService";
import type { Document } from "../../types";
import { EntityStatus } from "../../types";
import {
  ARCHITECTURE_ARTIFACT_LABELS,
  ARCHITECTURE_ARTIFACT_TYPES,
  ARCHITECTURE_LIFECYCLE_PHASES,
  ARCHITECTURE_PHASE_LABELS,
  ARCHITECTURE_STANDARD_CELL_COUNT,
  architectureIdentityKey,
  isArchitectureStandardDocument,
  parseArchitectureClassifications,
  type ArchitectureArtifactType,
  type ArchitectureLifecyclePhase,
} from "../../utils/architectureStandards";

const ArchitectureStandardsHubPage: React.FC = () => {
  const hubState = useBookmarkableHubState();
  const { documents: fetched, loading, error, refresh } = useDocuments();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [artifactFilter, setArtifactFilter] = useState("all");
  const [phaseFilter, setPhaseFilter] = useState("all");
  const [viewUuid, setViewUuid] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const [seedMessage, setSeedMessage] = useState<string | null>(null);
  const [toggleLoadingId, setToggleLoadingId] = useState<string | null>(null);

  useEffect(() => {
    setDocuments(fetched.filter(isArchitectureStandardDocument));
  }, [fetched]);

  const filtered = useMemo(() => {
    const base = filterByHubState(documents, hubState);
    return base.filter((doc) => {
      const parsed = parseArchitectureClassifications(doc.classifications);
      if (artifactFilter !== "all" && parsed.artifactType !== artifactFilter) {
        return false;
      }
      if (phaseFilter !== "all" && parsed.lifecyclePhase !== phaseFilter) {
        return false;
      }
      return true;
    });
  }, [documents, hubState, artifactFilter, phaseFilter]);

  const paginated = useMemo(() => {
    const start = (hubState.page - 1) * PAGE_SIZE;
    return filtered.slice(start, start + PAGE_SIZE);
  }, [filtered, hubState.page]);

  const existingKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const doc of documents) {
      const parsed = parseArchitectureClassifications(doc.classifications);
      if (parsed.artifactType && parsed.lifecyclePhase) {
        keys.add(architectureIdentityKey(parsed.artifactType, parsed.lifecyclePhase));
      }
    }
    return keys;
  }, [documents]);

  const missingCount = ARCHITECTURE_STANDARD_CELL_COUNT - existingKeys.size;

  const editDoc = useMemo(
    () => documents.find((d) => d.uuid === hubState.editUuid) ?? null,
    [documents, hubState.editUuid],
  );
  const viewDoc = useMemo(
    () => documents.find((d) => d.uuid === viewUuid) ?? null,
    [documents, viewUuid],
  );

  const handleToggle = async (doc: Document) => {
    setToggleLoadingId(doc.uuid);
    const newStatus =
      doc.status === EntityStatus.ACTIVE
        ? EntityStatus.INACTIVE
        : EntityStatus.ACTIVE;
    try {
      const updated = await apiService.updateDocument(doc.uuid, {
        name: doc.name,
        description: doc.description ?? undefined,
        status: newStatus,
      });
      setDocuments((prev) =>
        prev.map((d) => (d.uuid === updated.uuid ? updated : d)),
      );
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : "Failed to update status");
    } finally {
      setToggleLoadingId(null);
    }
  };

  const handleSeed = async () => {
    setSeeding(true);
    setSeedMessage(null);
    try {
      const result = await apiService.seedArchitectureStandards();
      setSeedMessage(
        `Starter pack: ${result.created} created, ${result.skipped} already present (${result.total} cells).`,
      );
      await refresh();
    } catch (err: unknown) {
      setSeedMessage(
        err instanceof Error ? err.message : "Failed to load starter pack",
      );
    } finally {
      setSeeding(false);
    }
  };

  return (
    <>
      <EntityHubLayout
        hubTitle="Architecture Standards"
        hubDescription="Design through decommissioning standards for microservices, Apigee, API management, and Kubernetes. Load the starter pack, then edit cells as the tenant source of truth."
        addLabel="New standard"
        onAdd={() => setCreating(true)}
        backPath="/agent-dashboard/architect"
        backLabel="Back to Architect Agent"
        headerActions={
          missingCount > 0 ? (
            <Button
              variant="outlined"
              startIcon={<PlaylistAddIcon />}
              disabled={seeding}
              onClick={handleSeed}
              sx={{ textTransform: "none" }}
            >
              {seeding ? "Loading starter pack…" : "Load starter pack"}
            </Button>
          ) : undefined
        }
        loading={loading}
        error={error}
        itemCount={filtered.length}
        hubState={hubState}
        filterSidebarExtra={
          <>
            <TextField
              select
              fullWidth
              size="small"
              label="Artifact type"
              value={artifactFilter}
              onChange={(e) => setArtifactFilter(e.target.value)}
              sx={{ mt: 2 }}
            >
              <MenuItem value="all">All artifacts</MenuItem>
              {ARCHITECTURE_ARTIFACT_TYPES.map((id) => (
                <MenuItem key={id} value={id}>
                  {ARCHITECTURE_ARTIFACT_LABELS[id]}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              fullWidth
              size="small"
              label="Lifecycle phase"
              value={phaseFilter}
              onChange={(e) => setPhaseFilter(e.target.value)}
              sx={{ mt: 2 }}
            >
              <MenuItem value="all">All phases</MenuItem>
              {ARCHITECTURE_LIFECYCLE_PHASES.map((id) => (
                <MenuItem key={id} value={id}>
                  {ARCHITECTURE_PHASE_LABELS[id]}
                </MenuItem>
              ))}
            </TextField>
            {missingCount > 0 && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
                {existingKeys.size} of {ARCHITECTURE_STANDARD_CELL_COUNT} starter cells present.
              </Typography>
            )}
          </>
        }
      >
        {seedMessage && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setSeedMessage(null)}>
            {seedMessage}
          </Alert>
        )}
        <Grid container spacing={2}>
          {paginated.map((doc) => {
            const parsed = parseArchitectureClassifications(doc.classifications);
            const artifactLabel =
              ARCHITECTURE_ARTIFACT_LABELS[
                parsed.artifactType as ArchitectureArtifactType
              ] ?? parsed.artifactType ?? "Artifact";
            const phaseLabel =
              ARCHITECTURE_PHASE_LABELS[
                parsed.lifecyclePhase as ArchitectureLifecyclePhase
              ] ?? parsed.lifecyclePhase ?? "Phase";
            return (
              <Grid item xs={12} sm={6} md={4} key={doc.uuid}>
                <EntityAssetCard
                  title={doc.name}
                  subtitle={doc.description ?? undefined}
                  status={doc.status}
                  metadata={
                    <>
                      <Chip size="small" label={artifactLabel} sx={{ mt: 0.5, mr: 0.5 }} />
                      <Chip size="small" label={phaseLabel} sx={{ mt: 0.5 }} />
                    </>
                  }
                  enabled={doc.status === EntityStatus.ACTIVE}
                  toggleLoading={toggleLoadingId === doc.uuid}
                  onToggleEnabled={() => handleToggle(doc)}
                  onView={() => setViewUuid(doc.uuid)}
                  onEdit={() => hubState.setEditUuid(doc.uuid)}
                  onDelete={async () => {
                    try {
                      await apiService.deleteDocument(doc.uuid);
                      setDocuments((prev) =>
                        prev.filter((d) => d.uuid !== doc.uuid),
                      );
                    } catch (err: unknown) {
                      alert(
                        err instanceof Error
                          ? err.message
                          : "Failed to delete standard",
                      );
                    }
                  }}
                />
              </Grid>
            );
          })}
          {!loading && filtered.length === 0 && (
            <Grid item xs={12}>
              <Typography color="text.secondary" align="center" sx={{ py: 4 }}>
                No architecture standards match your filters. Load the starter pack to create all 49 cells.
              </Typography>
            </Grid>
          )}
        </Grid>
      </EntityHubLayout>

      <ViewDocumentDialog
        document={viewDoc}
        open={Boolean(viewUuid && viewDoc)}
        onClose={() => setViewUuid(null)}
        onEdit={() => {
          if (viewUuid) hubState.setEditUuid(viewUuid);
          setViewUuid(null);
        }}
      />

      <EditArchitectureStandardDialog
        document={creating ? null : editDoc}
        creating={creating}
        open={creating || Boolean(hubState.editUuid && editDoc)}
        onClose={() => {
          setCreating(false);
          hubState.setEditUuid(null);
        }}
        onSaved={(updated) => {
          setDocuments((prev) => {
            const exists = prev.some((d) => d.uuid === updated.uuid);
            if (exists) {
              return prev.map((d) => (d.uuid === updated.uuid ? updated : d));
            }
            return [updated, ...prev];
          });
        }}
      />
    </>
  );
};

export default ArchitectureStandardsHubPage;
